import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Chrome,
  Puzzle,
  FolderOpen,
  CheckCircle2,
  ExternalLink,
  Copy,
  Check,
  KeyRound,
  RefreshCw,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { connectionCodesApi } from '@/lib/api';
import type { Session } from '@supabase/supabase-js';

const EXTENSION_VERSION = '1.0.0';
const CHROME_EXTENSIONS_URL = 'chrome://extensions';

export function ExtensionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [connectionCode, setConnectionCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const connectionSectionRef = useRef<HTMLDivElement>(null);
  const autoGenerateTriggered = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);

  // Show modal and auto-generate code when ?generate=true parameter is present
  useEffect(() => {
    const shouldGenerate = searchParams.get('generate') === 'true';
    if (shouldGenerate && session && !autoGenerateTriggered.current) {
      autoGenerateTriggered.current = true;
      setShowCodeModal(true);
      handleGenerateCode();
    }
  }, [searchParams, session]);

  const handleCloseModal = () => {
    setShowCodeModal(false);
    // Remove the generate parameter from URL
    searchParams.delete('generate');
    setSearchParams(searchParams);
  };

  const handleGenerateCode = async () => {
    if (!session) {
      toast.error('로그인이 필요합니다');
      return;
    }

    setIsGenerating(true);
    try {
      const { code } = await connectionCodesApi.create({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at,
      });
      setConnectionCode(code);
    } catch (error) {
      console.error('Failed to generate code:', error);
      toast.error('코드 생성에 실패했습니다');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyExtensionId = () => {
    const extensionId = import.meta.env.VITE_EXTENSION_ID || 'your-extension-id';
    navigator.clipboard.writeText(extensionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyExtensionsUrl = () => {
    navigator.clipboard.writeText(CHROME_EXTENSIONS_URL);
    setUrlCopied(true);
    toast.success('복사됨! 브라우저 주소창에 붙여넣기 하세요');
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const steps = [
    {
      icon: Download,
      title: '확장 프로그램 다운로드',
      description: '아래 버튼을 클릭하여 확장 프로그램 ZIP 파일을 다운로드하세요.',
    },
    {
      icon: FolderOpen,
      title: 'ZIP 파일 압축 해제',
      description: '다운로드한 ZIP 파일을 원하는 위치에 압축 해제하세요.',
    },
    {
      icon: Chrome,
      title: 'Chrome 확장 프로그램 페이지 열기',
      description: 'Chrome 브라우저에서 chrome://extensions 주소로 이동하세요.',
    },
    {
      icon: Puzzle,
      title: '개발자 모드 활성화',
      description: '우측 상단의 "개발자 모드" 토글을 켜세요.',
    },
    {
      icon: FolderOpen,
      title: '압축해제된 확장 프로그램 로드',
      description:
        '"압축해제된 확장 프로그램을 로드합니다" 버튼을 클릭하고, 압축 해제한 폴더를 선택하세요.',
    },
    {
      icon: CheckCircle2,
      title: '설치 완료!',
      description: 'Markly 확장 프로그램이 설치되었습니다. 브라우저 툴바에서 확인하세요.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Code Generation Modal */}
      <Dialog open={showCodeModal} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-emerald-500" />
              확장 프로그램 연결
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {isGenerating ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                <p className="mt-3 text-sm text-gray-500">인증 코드 생성 중...</p>
              </div>
            ) : connectionCode ? (
              <div className="space-y-4">
                <p className="text-center text-sm text-gray-600">
                  아래 코드를 확장 프로그램에 입력하세요
                </p>

                {/* 6-digit code display */}
                <div className="flex justify-center gap-2">
                  {connectionCode.split('').map((digit, i) => (
                    <div
                      key={i}
                      className="flex h-14 w-12 items-center justify-center rounded-lg border-2 border-emerald-300 bg-emerald-50 text-2xl font-bold text-gray-900"
                    >
                      {digit}
                    </div>
                  ))}
                </div>

                <p className="text-center text-xs text-gray-400">
                  5분 후 만료됩니다
                </p>

                <div className="flex justify-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(connectionCode);
                      toast.success('코드가 복사되었습니다');
                    }}
                    className="gap-1.5"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    복사
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setConnectionCode(null);
                      handleGenerateCode();
                    }}
                    disabled={isGenerating}
                    className="gap-1.5"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
                    새 코드
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-sm text-gray-500">코드 생성에 실패했습니다</p>
                <Button
                  onClick={handleGenerateCode}
                  className="mt-4 gap-2"
                  disabled={isGenerating}
                >
                  다시 시도
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-emerald-100 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-600 transition-colors hover:text-emerald-600"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">돌아가기</span>
          </Link>
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Markly" className="h-7 w-7" />
            <span className="font-semibold text-gray-900">Markly</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <img
            src="/images/logo.png"
            alt="Markly Extension"
            className="mx-auto h-20 w-20 object-contain"
          />
          <h1 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
            Markly Chrome 확장 프로그램
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-600">
            어디서든 클릭 한 번으로 북마크를 저장하세요.
            <br />
            브라우저 북마크도 손쉽게 가져올 수 있습니다.
          </p>

          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              className="gap-2 bg-emerald-500 text-white hover:bg-emerald-600"
              asChild
            >
              <a href="/downloads/markly-extension.zip" download>
                <Download className="h-5 w-5" />
                다운로드 (v{EXTENSION_VERSION})
              </a>
            </Button>
            <Button size="lg" variant="outline" className="gap-2" asChild>
              <a
                href="https://github.com/sumi-0011/markly"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-5 w-5" />
                GitHub에서 보기
              </a>
            </Button>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            Chrome, Edge, Brave 등 Chromium 기반 브라우저 지원
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-8 text-center text-2xl font-bold text-gray-900">주요 기능</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100">
                <svg
                  className="h-6 w-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 font-semibold text-gray-900">원클릭 저장</h3>
              <p className="text-sm text-gray-600">
                현재 페이지를 클릭 한 번으로 저장하세요. 제목, 설명, 썸네일이 자동으로 추출됩니다.
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100">
                <svg
                  className="h-6 w-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 font-semibold text-gray-900">자동 태그 추천</h3>
              <p className="text-sm text-gray-600">
                URL과 콘텐츠를 분석하여 관련 태그를 자동으로 추천합니다.
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100">
                <svg
                  className="h-6 w-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
              </div>
              <h3 className="mb-2 font-semibold text-gray-900">북마크 가져오기</h3>
              <p className="text-sm text-gray-600">
                브라우저에 저장된 북마크를 Markly로 한 번에 가져올 수 있습니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Steps */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-8 text-center text-2xl font-bold text-gray-900">설치 방법</h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  <span className="font-semibold text-emerald-600">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 flex items-center gap-2 font-semibold text-gray-900">
                    <step.icon className="h-4 w-4 text-emerald-500" />
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600">{step.description}</p>

                  {/* Special content for step 3 */}
                  {index === 2 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <code className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
                        {CHROME_EXTENSIONS_URL}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5"
                        onClick={handleCopyExtensionsUrl}
                      >
                        {urlCopied ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                        {urlCopied ? '복사됨' : '복사'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 gap-1.5 text-emerald-600 hover:text-emerald-700"
                        asChild
                      >
                        <a href={CHROME_EXTENSIONS_URL}>
                          <ExternalLink className="h-3.5 w-3.5" />
                          바로가기
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="bg-white px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-8 text-center text-2xl font-bold text-gray-900">미리보기</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-lg">
              <div className="bg-gray-100 px-4 py-2 text-sm text-gray-500">빠른 저장</div>
              <div className="flex aspect-[4/3] items-center justify-center bg-gray-50">
                <div className="p-8 text-center">
                  <img
                    src="/images/logo.png"
                    alt="Markly Extension"
                    className="mx-auto mb-4 h-20 w-20 object-contain"
                  />
                  <p className="text-sm text-gray-500">확장 프로그램 팝업 미리보기</p>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-lg">
              <div className="bg-gray-100 px-4 py-2 text-sm text-gray-500">북마크 가져오기</div>
              <div className="flex aspect-[4/3] items-center justify-center bg-gray-50">
                <div className="p-8 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-emerald-100">
                    <FolderOpen className="h-8 w-8 text-emerald-500" />
                  </div>
                  <p className="text-sm text-gray-500">브라우저 북마크 가져오기 화면</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Connection Code Section */}
      {session && (
        <section ref={connectionSectionRef} className="px-4 py-12">
          <div className="mx-auto max-w-4xl">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                  <KeyRound className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">확장 프로그램에 연결</h3>
                  <p className="text-sm text-gray-600">
                    자동 연결이 안 되는 브라우저에서 사용하세요
                  </p>
                </div>
              </div>

              {!connectionCode ? (
                <Button
                  onClick={handleGenerateCode}
                  disabled={isGenerating}
                  className="gap-2 bg-emerald-500 hover:bg-emerald-600"
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Puzzle className="h-4 w-4" />
                  )}
                  {isGenerating ? '생성 중...' : '인증 코드 생성'}
                </Button>
              ) : (
                <div className="space-y-4">
                  {/* 6-digit code display */}
                  <div className="rounded-lg bg-white p-6 border border-emerald-200 text-center">
                    <p className="text-sm text-gray-500 mb-3">인증 코드</p>
                    <div className="flex justify-center gap-2">
                      {connectionCode.split('').map((digit, i) => (
                        <div
                          key={i}
                          className="w-12 h-14 flex items-center justify-center bg-gray-50 border-2 border-emerald-300 rounded-lg text-2xl font-bold text-gray-900"
                        >
                          {digit}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">5분 후 만료됩니다</p>
                  </div>

                  {/* Instructions */}
                  <div className="flex items-start gap-3 rounded-lg bg-white p-4 border border-gray-200">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-xs font-bold flex-shrink-0">
                      ?
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">확장 프로그램에서 코드 입력하기</p>
                      <p className="text-xs text-gray-500 mt-1">
                        브라우저 툴바에서 Markly 아이콘 클릭 →{' '}
                        <span className="font-medium text-emerald-600">"인증 코드로 연결"</span> 클릭 → 위 코드 입력
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateCode}
                      disabled={isGenerating}
                      className="gap-1.5"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
                      새 코드 생성
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConnectionCode(null)}
                    >
                      닫기
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Extension ID Section (for developers) */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-6">
            <h3 className="mb-2 font-semibold text-gray-900">개발자 정보</h3>
            <p className="mb-4 text-sm text-gray-600">
              확장 프로그램 ID가 필요한 경우 아래를 참고하세요.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-700">
                {import.meta.env.VITE_EXTENSION_ID || 'Extension ID가 설정되지 않았습니다'}
              </code>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={handleCopyExtensionId}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? '복사됨' : '복사'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 px-4 py-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm text-gray-500">
            문제가 있으신가요?{' '}
            <a href="mailto:selina202015@gmaiil.com" className="text-emerald-600 hover:underline">
              selina202015@gmaiil.com
            </a>
            으로 문의해 주세요.
          </p>
        </div>
      </footer>
    </div>
  );
}
